'use client';

import Footer from '@/components/Footer';
import GeneratorHeroSection from '@/components/GeneratorHeroSection';
import Header from '@/components/Header';
import { GeneratorControls } from '@/components/sora/GeneratorControls';
import { GeneratorEnhance } from '@/components/sora/GeneratorEnhance';
import { GeneratorImageEnhance } from '@/components/sora/GeneratorImageEnhance';
import { GeneratorInput } from '@/components/sora/GeneratorInput';
import { useGenerator } from '@/hooks/useGenerator';
import { cn } from '@/lib/utils';
import { Loader2 } from 'lucide-react';
import dynamic from 'next/dynamic';
import React, { Suspense, useEffect, useState } from 'react';
import '@/styles/generator.css';

const ExploreGallery = dynamic(
  () => import('@/components/sora/ExploreGallery').then((mod) => mod.ExploreGallery),
  {
    ssr: false,
    loading: () => <div className="h-[600px] w-full animate-pulse rounded-xl bg-gray-900/30" />,
  }
);
const UserGallery = dynamic(
  () => import('@/components/sora/UserGallery').then((mod) => mod.UserGallery),
  {
    ssr: false,
    loading: () => <div className="h-[400px] w-full animate-pulse rounded-xl bg-gray-900/30" />,
  }
);
const GeneratorModals = dynamic(
  () => import('@/components/sora/GeneratorModals').then((mod) => mod.GeneratorModals),
  { ssr: false }
);

import type { GeneratorMetadata } from '@/lib/types/generator';

export function GeneratorClient({ generator }: { generator?: GeneratorMetadata }) {
  const {
    isAuthenticated,
    user,
    isSignupModalOpen,
    setIsSignupModalOpen,
    isPricingModalOpen,
    setIsPricingModalOpen,
    isTrialModalOpen,
    setIsTrialModalOpen,
    isImageModalOpen,
    setIsImageModalOpen,
    subscriptionInfo,
    userContent,
    setUserContent,
    model,
    aspectRatio,
    resolution,
    duration,
    variations,
    mode,
    outputFormat,
    contentInput,
    setContentInput,
    selectedImage,
    setSelectedImage,
    selectedImages,
    setSelectedImages,
    services,
    estimatedCost,
    activeDropdown,
    toggleDropdown,
    expandedSection: openSection,
    setExpandedSection: setOpenSection,
    isSubmitting,
    activeGenerations,
    showEncouragement,
    isInitialLoading,
    isAuthInitialized,
    contentEditableRef,
    controlsRef,
    containerRef,
    handleInput,
    handleSubmit,
    selectOption,
    refreshCreditsAndSubscription,
    autoSwitchEnabled,
    isFreePreviewEligible,
  } = useGenerator(generator);

  const toggleSection = (section: string) => {
    setOpenSection(openSection === section ? null : section);
  };

  const MAX_CHARS = 1800;

  // Force close chatbot if it was opened on another page
  useEffect(() => {
    try {
      // @ts-ignore
      if (window.Typebot) {
        // @ts-ignore
        window.Typebot.close();
      }
    } catch (e) {}
  }, []);

  const [hasScrolledForDynamicContent, setHasScrolledForDynamicContent] = useState(false);

  // Auto-focus logic
  useEffect(() => {
    if (window.innerWidth < 768) {
      if (document.activeElement === contentEditableRef.current) {
        contentEditableRef.current?.blur();
      }
    } else {
      const timer = setTimeout(() => {
        contentEditableRef.current?.focus({ preventScroll: true });
      }, 600);
      return () => clearTimeout(timer);
    }
  }, [contentEditableRef]);

  // Anchor scroll correction for dynamic content shifts
  useEffect(() => {
    const hash = window.location.hash;
    if (!hash || hasScrolledForDynamicContent) return;

    // Wait until galleries are likely initialized or finished loading
    if (!isInitialLoading) {
      const targetId = hash.replace('#', '');
      const element = document.getElementById(targetId);

      if (element) {
        // Delay slightly for any final layout paints
        const timer = setTimeout(() => {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          setHasScrolledForDynamicContent(true);
        }, 500);
        return () => clearTimeout(timer);
      }
    }
  }, [isInitialLoading, hasScrolledForDynamicContent]);

  // Reset scroll tracker on hash change to allow re-triggering if clicking menu again
  useEffect(() => {
    const handleHashChange = () => setHasScrolledForDynamicContent(false);
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  return (
    <>
      <main id="main-content" className="min-h-screen md:pt-0">
        <Suspense
          fallback={
            <div className="fixed top-0 right-0 left-0 z-50 h-[72px] border-black/5 border-b bg-white shadow-sm" />
          }
        >
          <Header />
        </Suspense>

        {!isAuthenticated && <GeneratorHeroSection generator={generator} />}

        <section
          id="generator"
          className={cn('generator pt-[2px] md:pt-0', isAuthenticated && 'pt-24 md:pt-20')}
          style={{ scrollMarginTop: '100px' }}
        >
          <div className="container">
            <div
              className={`generator-interface relative mb-12${contentInput.trim() ? ' generator-ready' : ''}`}
            >
              <GeneratorControls
                user={user}
                services={services}
                model={model}
                aspectRatio={aspectRatio}
                resolution={resolution}
                duration={duration}
                variations={variations}
                mode={mode}
                outputFormat={outputFormat}
                activeDropdown={activeDropdown}
                toggleDropdown={toggleDropdown}
                selectOption={selectOption}
                setOpenSection={setOpenSection}
                controlsRef={controlsRef}
                autoSwitchEnabled={autoSwitchEnabled}
                selectedImage={selectedImage}
                selectedImages={selectedImages}
                enhanceContent={
                  model === 'nano-banana-2' || model === 'z-image' ? (
                    <GeneratorImageEnhance
                      activeDropdown={activeDropdown}
                      openSection={openSection}
                      toggleSection={toggleSection}
                      selectOption={selectOption}
                      setOpenSection={setOpenSection}
                    />
                  ) : (
                    <GeneratorEnhance
                      activeDropdown={activeDropdown}
                      openSection={openSection}
                      toggleSection={toggleSection}
                      selectOption={selectOption}
                      setOpenSection={setOpenSection}
                    />
                  )
                }
              />

              <GeneratorInput
                model={model}
                isAuthenticated={isAuthenticated}
                selectedImage={selectedImage}
                setSelectedImage={setSelectedImage}
                selectedImages={selectedImages}
                setSelectedImages={setSelectedImages}
                setIsImageModalOpen={setIsImageModalOpen}
                setIsSignupModalOpen={setIsSignupModalOpen}
                videoInput={contentInput}
                setVideoInput={setContentInput}
                handleInput={handleInput}
                contentEditableRef={contentEditableRef}
                containerRef={containerRef}
                showEncouragement={showEncouragement}
                activeGenerations={activeGenerations}
                estimatedCost={estimatedCost}
                handleSubmit={handleSubmit}
                isSubmitting={isSubmitting}
                MAX_CHARS={MAX_CHARS}
              />
            </div>

            {isAuthenticated && (
              <>
                <h2
                  className="section-title explore-title"
                  id="gallery"
                  style={{
                    background: 'linear-gradient(135deg, #e2e8f0, #94a3b8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Gallery
                </h2>
                <div className="video-gallery mb-12">
                  <UserGallery
                    userContent={userContent}
                    activeGenerations={activeGenerations}
                    contentInput={contentInput}
                    setContentInput={setContentInput}
                    isLoading={isInitialLoading}
                    isAuthInitialized={isAuthInitialized}
                    onDismiss={(contentId: string) => {
                      setUserContent((prev: any[]) => prev.filter((v: any) => v.id !== contentId));
                    }}
                    services={services}
                  />
                </div>
              </>
            )}

            {generator?.explore && (
              <>
                <h2
                  className="section-title explore-title text-center"
                  id="explore"
                  style={{
                    scrollMarginTop: '140px',
                    background: 'linear-gradient(135deg, #e2e8f0, #94a3b8)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    backgroundClip: 'text',
                  }}
                >
                  Explore {generator?.shortName}
                </h2>
                <ExploreGallery exploreData={generator?.explore} />
                <div className="my-10 flex justify-center">
                  <button
                    className="btn btn-primary"
                    onClick={() => {
                      const el = document.getElementById('generator');
                      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }}
                  >
                    Start Generating
                  </button>
                </div>
              </>
            )}
          </div>
        </section>

        <GeneratorModals
          isSignupModalOpen={isSignupModalOpen}
          setIsSignupModalOpen={setIsSignupModalOpen}
          isPricingModalOpen={isPricingModalOpen}
          setIsPricingModalOpen={setIsPricingModalOpen}
          isTrialModalOpen={isTrialModalOpen}
          setIsTrialModalOpen={setIsTrialModalOpen}
          activeGenerations={activeGenerations}
          contentInput={contentInput}
          setContentInput={setContentInput}
          subscriptionInfo={subscriptionInfo}
          refreshCreditsAndSubscription={refreshCreditsAndSubscription}
          isImageModalOpen={isImageModalOpen}
          setIsImageModalOpen={setIsImageModalOpen}
          aspectRatio={aspectRatio}
          model={model}
          setSelectedImage={setSelectedImage}
          selectedImages={selectedImages}
          setSelectedImages={setSelectedImages}
          selectOption={selectOption}
          userContent={userContent}
          isFreePreviewEligible={isFreePreviewEligible}
        />

        <Suspense fallback={<div className="h-20" />}>
          <Footer />
        </Suspense>
      </main>
    </>
  );
}
